
import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { UserTypeFilter } from './usePaginatedMediaData';

type VerificationStatus = 'all' | 'pending' | 'waiting' | 'approved' | 'rejected';

interface EnrichedVerification {
  id: string;
  user_id: string;
  username: string;
  user_photo_url?: string;
  status: string;
  submitted_at: string;
  reviewed_at?: string;
  reviewed_by?: string;
  reviewer_username?: string;
  selfie_photo_url?: string;
  email_verified: boolean;
  email_verified_at?: string;
  rejection_reason?: string;
  created_at: string;
  ai_analysis?: any;
  // Profile data
  bio?: string;
  birth_date?: string;
  gender?: string;
  city?: string;
  state_code?: string;
  main_photo_url?: string;
  profile_photos?: string[];
}

export const useAdminVerifications = (userTypeFilter?: UserTypeFilter) => {
  const [statusFilter, setStatusFilter] = useState<VerificationStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const { 
    data: verifications = [], 
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['admin-verifications', statusFilter, userTypeFilter],
    queryFn: async () => {
      debugLogger.debug('[AdminVerifications] Fetching verifications with filter:', { statusFilter, userTypeFilter });
      
      // Step 1: Fetch document_verifications
      let verificationsQuery = supabase
        .from('document_verifications')
        .select('*')
        .order('submitted_at', { ascending: false });

      // Apply status filter
      if (statusFilter !== 'all') {
        verificationsQuery = verificationsQuery.eq('status', statusFilter);
      }

      const { data: verificationsData, error: verificationsError } = await verificationsQuery;
      
      if (verificationsError) throw verificationsError;
      if (!verificationsData || verificationsData.length === 0) return [];

      // Step 2: Extract unique user IDs
      const userIds = [...new Set(verificationsData.map(v => v.user_id))];

      // Step 3: Fetch profiles with filters applied
      let profilesQuery = supabase
        .from('profiles')
        .select('id, username, main_photo_url, profile_photos, bio, birth_date, gender, city, state_code, role, plan_type, hidden, onboarding_completed')
        .in('id', userIds);

      // Apply user type filter to profiles query
      if (userTypeFilter && userTypeFilter !== 'all') {
        switch (userTypeFilter) {
          case 'sponsors':
            profilesQuery = profilesQuery.eq('role', 'sponsor');
            break;
          case 'sponsored':
            profilesQuery = profilesQuery.eq('role', 'sponsored');
            break;
          case 'free':
            profilesQuery = profilesQuery.in('plan_type', ['free', 'sponsor_free', 'sponsored_free']);
            break;
          case 'standard':
            profilesQuery = profilesQuery.eq('plan_type', 'sponsor_standard');
            break;
          case 'premium':
            profilesQuery = profilesQuery.in('plan_type', ['sponsor_premium', 'sponsored_premium']);
            break;
          case 'active':
            profilesQuery = profilesQuery.eq('hidden', false).eq('onboarding_completed', true);
            break;
          case 'pending':
            profilesQuery = profilesQuery.eq('onboarding_completed', false);
            break;
          case 'hidden':
            profilesQuery = profilesQuery.eq('hidden', true);
            break;
          case 'admins':
            // Get admin user IDs from user_roles
            const { data: roleData } = await supabase
              .from('user_roles')
              .select('user_id')
              .in('role', ['admin', 'moderator']);
            const adminIds = roleData?.map(r => r.user_id) || [];
            if (adminIds.length > 0) {
              profilesQuery = profilesQuery.in('id', adminIds);
            } else {
              // No admins found, return empty
              return [];
            }
            break;
          case 'test':
            profilesQuery = profilesQuery.ilike('username', 'test-%');
            break;
        }
      } else if (!userTypeFilter || userTypeFilter === 'all') {
        // Exclude test accounts by default
        profilesQuery = profilesQuery.not('username', 'ilike', 'test-%');
      }

      const { data: profilesData, error: profilesError } = await profilesQuery;
      
      if (profilesError) throw profilesError;

      // Step 4: Create profiles map and filter verifications
      const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
      const filteredUserIds = new Set(profilesData?.map(p => p.id) || []);
      
      // Filter verifications to include only those with matching profiles
      const data = verificationsData.filter(v => filteredUserIds.has(v.user_id));

      // Get reviewer usernames
      const reviewerIds = [...new Set(data.map(v => v.reviewed_by).filter(Boolean))];
      let reviewerProfilesMap = new Map();
      
      if (reviewerIds.length > 0) {
        const { data: reviewerProfiles } = await supabase
          .from('profiles')
          .select('id, username')
          .in('id', reviewerIds);
        
        reviewerProfilesMap = new Map(reviewerProfiles?.map(p => [p.id, p]) || []);
      }
      
      interface ProfileData {
        username?: string;
        main_photo_url?: string;
        bio?: string;
        birth_date?: string;
        gender?: string;
        city?: string;
        state_code?: string;
        profile_photos?: string[];
      }

      const enrichedVerifications: EnrichedVerification[] = data.map((v: any) => {
        const userProfile = profilesMap.get(v.user_id) as ProfileData | undefined;
        const reviewerProfile = v.reviewed_by ? reviewerProfilesMap.get(v.reviewed_by) as { username?: string } | undefined : null;
        
        // Transform selfie URL from relative path to full public URL
        const selfieUrl = v.selfie_photo_url 
          ? supabase.storage
              .from('verification-documents')
              .getPublicUrl(v.selfie_photo_url).data.publicUrl
          : undefined;
        
        return {
          id: v.id,
          user_id: v.user_id,
          status: v.status,
          submitted_at: v.submitted_at,
          reviewed_at: v.reviewed_at,
          reviewed_by: v.reviewed_by,
          email_verified: v.email_verified,
          email_verified_at: v.email_verified_at,
          selfie_photo_url: selfieUrl,
          ai_analysis: v.ai_analysis,
          rejection_reason: v.rejection_reason,
          created_at: v.created_at,
          username: userProfile?.username || 'Usuário',
          user_photo_url: userProfile?.main_photo_url,
          reviewer_username: reviewerProfile?.username,
          bio: userProfile?.bio,
          birth_date: userProfile?.birth_date,
          gender: userProfile?.gender,
          city: userProfile?.city,
          state_code: userProfile?.state_code,
          main_photo_url: userProfile?.main_photo_url,
          profile_photos: userProfile?.profile_photos || []
        };
      });

      debugLogger.debug('[AdminVerifications] Loaded verifications:', enrichedVerifications.length);
      return enrichedVerifications;
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
  });

  // Filter by search term
  const filteredVerifications = verifications.filter(verification =>
    verification.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status counts
  const statusCounts = verifications.reduce((acc, verification) => {
    acc[verification.status] = (acc[verification.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const handleVerificationAction = useCallback(async (
    action: 'approve' | 'reject' | 'reset', 
    verificationId: string,
    rejectionReason?: string
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      let updateData: any = {
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id
      };

      switch (action) {
        case 'approve':
          updateData.status = 'approved';
          updateData.rejection_reason = null; // Clear any previous rejection reason
          break;
        case 'reject':
          updateData.status = 'rejected';
          updateData.rejection_reason = rejectionReason || 'Documentos não atendem aos critérios';
          // Reset selfie verification to allow user to take a new selfie
          updateData.selfie_verified = false;
          updateData.selfie_verified_at = null;
          updateData.ai_analysis = null;
          break;
        case 'reset':
          updateData.status = 'pending';
          updateData.rejection_reason = null;
          updateData.reviewed_at = null;
          updateData.reviewed_by = null;
          updateData.submitted_at = null;
          // Reset selfie verification to allow user to take a new selfie
          updateData.selfie_verified = false;
          updateData.selfie_verified_at = null;
          updateData.ai_analysis = null;
          // Keep selfie_photo_url so user can see their old photo
          break;
      }

      const { error } = await supabase
        .from('document_verifications')
        .update(updateData)
        .eq('id', verificationId);

      if (error) throw error;

      // Update verification_level in profiles table based on new status
      const { data: verification } = await supabase
        .from('document_verifications')
        .select('user_id')
        .eq('id', verificationId)
        .single();

      if (verification) {
        let newVerificationLevel = 'basic';
        switch (updateData.status) {
          case 'approved':
            newVerificationLevel = 'full';
            break;
          case 'waiting':
            newVerificationLevel = 'waiting';
            break;
          case 'rejected':
            newVerificationLevel = 'rejected';
            break;
          case 'pending':
            newVerificationLevel = 'basic';
            break;
        }

        await supabase
          .from('profiles')
          .update({ verification_level: newVerificationLevel })
          .eq('id', verification.user_id);
      }

      debugLogger.debug('[AdminVerifications] Action completed:', { action, verificationId });
      await refetch();
      return true;
    } catch (error) {
      debugLogger.error('[AdminVerifications] Error in action:', error);
      throw error;
    }
  }, [refetch]);

  return {
    verifications: filteredVerifications,
    isLoading,
    error,
    refetch,
    statusFilter,
    setStatusFilter,
    searchTerm,
    setSearchTerm,
    statusCounts,
    handleVerificationAction
  };
};
